import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Smile, Facebook, Instagram, Youtube, Linkedin, MessageSquare, Globe, X, Mail, Phone, MapPin, Calendar, Clock, Award, Star, Heart, ThumbsUp, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { BrandTiktok } from '@/components/profile/SocialMediaIcons';

interface IconPickerProps {
  onIconSelect: (iconSvg: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ onIconSelect }) => {
  const [open, setOpen] = useState(false);

  const iconsList = [
    { name: 'Facebook', icon: Facebook, color: '#1877F2' },
    { name: 'Instagram', icon: Instagram, color: '#E4405F' },
    { name: 'YouTube', icon: Youtube, color: '#FF0000' },
    { name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
    { name: 'X/Twitter', icon: X, color: '#000000' },
    { name: 'TikTok', icon: BrandTiktok, color: '#000000' },
    { name: 'Snapchat', icon: MessageSquare, color: '#FFFC00' },
    { name: 'Website', icon: Globe, color: '#4B5563' },
    { name: 'Email', icon: Mail, color: '#002E62' },
    { name: 'Phone', icon: Phone, color: '#002E62' },
    { name: 'Location', icon: MapPin, color: '#002E62' },
    { name: 'Calendar', icon: Calendar, color: '#BC8D09' },
    { name: 'Clock', icon: Clock, color: '#BC8D09' },
    { name: 'Award', icon: Award, color: '#BC8D09' },
    { name: 'Star', icon: Star, color: '#BC8D09' },
    { name: 'Heart', icon: Heart, color: '#EF4444' },
    { name: 'Thumbs Up', icon: ThumbsUp, color: '#10B981' },
    { name: 'Check', icon: CheckCircle, color: '#10B981' },
    { name: 'Alert', icon: AlertCircle, color: '#F59E0B' },
    { name: 'Info', icon: Info, color: '#3B82F6' },
  ];

  const handleIconClick = (IconComponent: any, color: string) => {
    // Create a temporary div to render the icon and get its SVG
    const tempDiv = document.createElement('div');
    const iconElement = React.createElement(IconComponent, { 
      size: 24, 
      color: color,
      strokeWidth: 2
    });
    
    // Convert React element to SVG string
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: middle; margin: 0 4px;">${getSvgPath(IconComponent)}</svg>`;
    
    onIconSelect(svgString);
    setOpen(false);
  };

  const getSvgPath = (IconComponent: any) => {
    // Map of icon components to their SVG paths
    const iconPaths: Record<string, string> = {
      Facebook: '<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>',
      Instagram: '<rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>',
      Youtube: '<path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"></path><path d="m10 15 5-3-5-3z"></path>',
      Linkedin: '<path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle>',
      X: '<path d="M4 4l11.733 16h4.267l-11.733 -16z"></path><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"></path>',
      BrandTiktok: '<path d="M9 12a4 4 0 1 0 4 4V4c0-1.1.9-2 2-2h2"></path><path d="M8 16a4 4 0 0 1 8 0v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4z"></path>',
      MessageSquare: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>',
      Globe: '<circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path>',
      Mail: '<rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>',
      Phone: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>',
      MapPin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle>',
      Calendar: '<path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path>',
      Clock: '<circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>',
      Award: '<circle cx="12" cy="8" r="6"></circle><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path>',
      Star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>',
      Heart: '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>',
      ThumbsUp: '<path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>',
      CheckCircle: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path>',
      AlertCircle: '<circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line>',
      Info: '<circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path>',
    };
    
    return iconPaths[IconComponent.name] || '';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-2"
        >
          <Smile className="h-4 w-4" />
          Insert Icon
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Select an icon</h4>
          <div className="grid grid-cols-5 gap-2">
            {iconsList.map(({ name, icon: Icon, color }) => (
              <Button
                key={name}
                variant="outline"
                size="sm"
                className="h-12 w-12 p-0 hover:bg-accent"
                onClick={() => handleIconClick(Icon, color)}
                title={name}
              >
                <Icon size={20} color={color} />
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IconPicker;
